import { useState } from "react";
import axios from "axios";
import { Lock, KeyRound, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RaiseSection() {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/investor/auth`, {
        password: password.trim(),
      });
      if (res.data?.ok) {
        setDetails(res.data.raise_details);
        setUnlocked(true);
        toast.success("Access granted", {
          description: "Investor view unlocked.",
        });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Invalid access code. Try again.";
      setError(msg);
      toast.error("Access denied", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="raise"
      data-testid="raise-section"
      className="relative border-b border-line bg-white paper-grain"
    >
      <div className="w-full max-w-none px-4 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32">
        <div className="flex items-center justify-between mb-14">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
            04 / The Raise
          </span>
          <div className="flex items-center gap-2 text-ink-soft">
            <Lock className="w-3.5 h-3.5" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em]">
              {unlocked ? "Investor view · unlocked" : "Gated · investor view"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <h2
              data-testid="raise-headline"
              className="font-display font-bold tracking-tight text-ink text-[1.75rem] sm:text-[2rem] lg:text-[2.25rem] leading-[1.2]"
            >
              We're not building
              <br />
              a campaign.
              <br />
              <span className="text-brand-orange">
                We're building infrastructure.
              </span>
            </h2>
            <p className="mt-8 text-lg sm:text-xl text-ink-soft leading-relaxed max-w-xl">
              The difference is that infrastructure compounds. This section is
              gated to qualified investors. Request the access code or proceed
              below.
            </p>

            <div className="mt-10 space-y-0 border-t border-line max-w-md">
              {[
                { k: "Stage", v: "Pre-seed" },
                { k: "Instrument", v: "SAFE / Priced round" },
                { k: "Use of funds", v: "Fractional CTO · metadata arch · NA launch" },
                { k: "Status", v: "In formation" },
              ].map((row) => (
                <div
                  key={row.k}
                  className="py-4 border-b border-line flex items-start justify-between gap-6"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                    {row.k}
                  </span>
                  <span className="font-mono text-sm text-ink text-right max-w-[60%]">
                    {row.v}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Gate / Detail card */}
          <div className="lg:col-span-6">
            {!unlocked ? (
              <div
                data-testid="investor-gate"
                className="border border-line bg-white"
              >
                <div className="flex items-center justify-between px-6 py-3 border-b border-line bg-white text-ink border border-line">
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-3.5 h-3.5 text-brand-orange" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
                      Investor access
                    </span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                    Encrypted · gated
                  </span>
                </div>

                <form onSubmit={submit} className="p-6 sm:p-10 space-y-6">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft mb-3">
                      Access code
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter investor code"
                      data-testid="investor-password-input"
                      className="w-full bg-white border border-line px-4 py-4 font-mono text-sm text-ink placeholder:text-ink-soft/60 focus:outline-none focus:bg-white focus:ring-2 focus:ring-brand-orange/50"
                    />
                    {error && (
                      <p
                        data-testid="investor-error"
                        className="mt-2 font-mono text-[11px] text-brand-red"
                      >
                        {error}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !password.trim()}
                    data-testid="investor-submit"
                    className="w-full inline-flex items-center justify-center gap-3 border border-line bg-white text-ink border border-line font-mono uppercase text-xs tracking-[0.22em] px-6 py-4 hover:bg-brand-orange hover:border-brand-orange transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Verifying..." : "Unlock investor view"}
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  <div className="flex items-start gap-2 text-ink-soft pt-2 border-t border-line">
                    <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-brand-orange shrink-0" />
                    <p className="text-xs leading-relaxed">
                      Don't have a code? Contact us at{" "}
                      <a
                        href="mailto:invest@shescan.app"
                        className="text-ink link-under"
                      >
                        invest@shescan.app
                      </a>{" "}
                      for data room access.
                    </p>
                  </div>
                </form>
              </div>
            ) : (
              <div
                data-testid="investor-details"
                className="border border-line bg-white animate-fade-up"
              >
                <div className="flex items-center justify-between px-6 py-3 border-b border-line bg-brand-orange text-ink">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
                      Investor view · unlocked
                    </span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                    Confidential
                  </span>
                </div>

                <div className="p-6 sm:p-10 space-y-10">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                      Round size
                    </div>
                    <div className="font-mono tracking-tighter text-6xl sm:text-7xl text-ink mt-1">
                      $600K
                    </div>
                    <p className="mt-2 text-sm text-ink-soft">
                      Pre-seed capital raise.
                    </p>
                  </div>

                  <div className="border-t border-line pt-8">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                      Pre-money valuation
                    </div>
                    <div className="font-mono tracking-tighter text-5xl sm:text-6xl text-brand-orange mt-1">
                      $5.0M
                    </div>
                  </div>

                  <div className="border-t border-line pt-8 space-y-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                      Use of funds · primary
                    </div>
                    <p className="text-ink leading-relaxed">
                      {details?.use_of_funds}
                    </p>
                  </div>

                  <div className="border-t border-line pt-8 space-y-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                      Thesis
                    </div>
                    <p className="font-display font-bold text-ink text-2xl leading-tight">
                      {details?.thesis}
                    </p>
                  </div>

                  <a
                    href="mailto:invest@shescan.app"
                    data-testid="investor-contact"
                    className="w-full inline-flex items-center justify-center gap-3 border border-line bg-white text-ink border border-line font-mono uppercase text-xs tracking-[0.22em] px-6 py-4 hover:bg-brand-orange hover:border-brand-orange transition-colors duration-150"
                  >
                    Request data room
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
